/* Standalone compatibility interactions; no Impulse, jQuery, Flickity or Swiper globals. */
if (!customElements.get('mr-portable-carousel')) {
  customElements.define('mr-portable-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.track = this.querySelector('[data-mr-slider-track]');
      this.slides = [...this.querySelectorAll('[data-mr-slide]')];
      this.dots = [...this.querySelectorAll('[data-mr-dot]')];
      this.index = 0;
      this.paused = false;
      this.hovered = false;
      if (!this.track || !this.slides.length) return;
      this.querySelector('[data-mr-prev]')?.addEventListener('click', () => this.move(this.index - 1), options);
      this.querySelector('[data-mr-next]')?.addEventListener('click', () => this.move(this.index + 1), options);
      this.dots.forEach(dot => dot.addEventListener('click', () => this.move(Number(dot.dataset.mrDot)), options));
      this.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (event.target.matches('input,textarea,select')) return;
        event.preventDefault();
        this.move(this.index + (event.key === 'ArrowLeft' ? -1 : 1));
      }, options);
      this.querySelector('[data-mr-pause]')?.addEventListener('click', event => {
        this.paused = !this.paused;
        event.currentTarget.setAttribute('aria-pressed', String(this.paused));
        event.currentTarget.setAttribute('aria-label', this.paused ? 'Play slideshow' : 'Pause slideshow');
        event.currentTarget.textContent = this.paused ? 'Play' : 'Pause';
        this.resetTimer();
      }, options);
      this.addEventListener('mouseenter', () => { this.hovered = true; this.resetTimer(); }, options);
      this.addEventListener('mouseleave', () => { this.hovered = false; this.resetTimer(); }, options);
      this.addEventListener('focusin', () => { this.focused = true; this.resetTimer(); }, options);
      this.addEventListener('focusout', event => {
        this.focused = this.contains(event.relatedTarget);
        this.resetTimer();
      }, options);
      document.addEventListener('visibilitychange', () => this.resetTimer(), options);
      if (this.dataset.mode === 'scroll') {
        let frame;
        this.track.addEventListener('scroll', () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => {
            const trackLeft = this.track.getBoundingClientRect().left;
            let closest = 0;
            let distance = Infinity;
            this.slides.forEach((slide, index) => {
              const current = Math.abs(slide.getBoundingClientRect().left - trackLeft);
              if (current < distance) { distance = current; closest = index; }
            });
            this.index = closest;
            this.updateDots();
          });
        }, options);
      } else {
        this.updateSlides();
        this.resetTimer();
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.timer);
    }
    move(index) {
      this.index = (index + this.slides.length) % this.slides.length;
      if (this.dataset.mode === 'scroll') {
        const slide = this.slides[this.index];
        const left = slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
        this.track.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        this.updateDots();
      } else {
        this.updateSlides();
        this.resetTimer();
      }
    }
    updateSlides() {
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
      this.updateDots();
    }
    updateDots() {
      this.dots.forEach(dot => dot.setAttribute('aria-current', String(Number(dot.dataset.mrDot) === this.index)));
    }
    resetTimer() {
      clearTimeout(this.timer);
      if (this.dataset.mode !== 'slideshow' || this.dataset.autoplay !== 'true' || this.slides.length < 2 || this.paused || this.hovered || this.focused || document.hidden || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.timer = setTimeout(() => this.move(this.index + 1), Number(this.dataset.speed) || 5000);
    }
  });
}
// The original literal review HTML/Liquid/script string is never changed. Shopify editor
// section replacement may not run its inline script; this fallback respects its ready flag.
function initializeLiteralReviews(root) {
  const sections = root.matches?.('#mr-customer-reviews') ? [root] : [...root.querySelectorAll('#mr-customer-reviews')];
  for (const section of sections) {
    if (section.dataset.ready === 'true') continue;
    const track = section.querySelector('[data-mr-track]');
    const previous = section.querySelector('[data-mr-prev]');
    const next = section.querySelector('[data-mr-next]');
    if (!track || !previous || !next) continue;
    section.dataset.ready = 'true';
    const move = direction => {
      const card = track.querySelector('.mr-card');
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      track.scrollBy({ left: direction * (card ? card.getBoundingClientRect().width + gap : track.clientWidth * .8), behavior: 'smooth' });
    };
    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
  }
}
initializeLiteralReviews(document);
document.addEventListener('shopify:section:load', event => initializeLiteralReviews(event.target));
